// Public pricing page — replaces the /tarifs → /pricing redirect for
// logged-out visitors. Logged-in users still bounce to /pricing where the
// product-side BillingPlansPage handles plan changes against their account.
//
// Sections, in order: A hero + billing toggle, B plan cards (Starter,
// Growth-popular, Network, Agency + Enterprise CTA), C credit packs,
// D collapsible feature matrix, E pricing-FAQ slice, F final CTA banner.

import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { CTABanner } from "@/components/marketing/CTABanner";
import { Rule } from "@/components/marketing/Rule";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useAuth } from "@/hooks/useAuth";
import { buildBreadcrumbJsonLd, buildFAQPageJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import {
  CREDIT_PACKS,
  PLAN_AUDIENCE,
  PLAN_DISPLAY_NAMES,
  PLAN_MATRIX,
  PLAN_SLUGS,
  type PlanSlug,
  formatCell,
  locText,
} from "@/lib/marketing-plan-matrix";
import { entriesByCategory, entryAnchor, faqAnswer, faqQuestion } from "@/lib/marketing-faq";
import { useLocale, intlLocale, type Locale } from "@/lib/locale";
import { formatMoney, planMonthlyCents, planSetupCents, creditPackCents, annualMonthlyEquivalentCents } from "@/lib/currency";

const VISIBLE_PLANS: PlanSlug[] = ["starter", "growth", "network", "agency"];

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const locale = useLocale();
  const [annual, setAnnual] = useState(false);

  // FAQ slice used in section E — only the "tarifs" category. The full
  // FAQPage JSON-LD is emitted on /faq; here we keep the page focused.
  const tarifsFaq = useMemo(() => entriesByCategory("tarifs"), []);

  // Logged-in users get sent to the product-side pricing page where they
  // can manage their actual subscription. We still render this marketing
  // page for logged-out visitors and during auth-loading we render the
  // marketing version (it is faster than blocking on auth).
  if (!authLoading && user) {
    return <Navigate to="/pricing" replace />;
  }

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildBreadcrumbJsonLd([
      { name: t("marketing.breadcrumbs.home"), path: "/" },
      { name: t("marketing.header.nav.pricing"), path: "/tarifs" },
    ]),
    buildFAQPageJsonLd(
      tarifsFaq.map((e) => ({
        question: faqQuestion(e, locale),
        answer: faqAnswer(e, locale),
      })),
    ),
  ];

  return (
    <>
      <SeoHead
        title={t("marketing.pages.tarifs.seo.title")}
        description={t("marketing.pages.tarifs.seo.description")}
        path="/tarifs"
        locale={locale}
        jsonLd={jsonLd}
      />

      {/* ── Section A — Hero + billing toggle ───────────────────────────── */}
      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.tarifs.hero.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">
            {t("marketing.pages.tarifs.hero.title")}
          </DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
            {t("marketing.pages.tarifs.hero.lead")}
          </p>
        </div>

        <div className="mt-10 flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-muted-foreground">
            {t("marketing.pages.tarifs.hero.trialReassure")}{" "}
            <Link to="/tarifs/calculateur" className="underline underline-offset-4 hover:text-foreground">
              {t("marketing.pages.tarifs.hero.calculatorLink")}
            </Link>
          </p>
          <div className="flex items-center gap-3">
            <Label htmlFor="pricing-billing-toggle" className={!annual ? "font-medium" : "text-muted-foreground"}>
              {t("marketing.pages.tarifs.hero.billingMonthly")}
            </Label>
            <Switch
              id="pricing-billing-toggle"
              checked={annual}
              onCheckedChange={setAnnual}
              aria-label={t("marketing.pages.tarifs.hero.billingAnnual")}
            />
            <Label htmlFor="pricing-billing-toggle" className={annual ? "font-medium" : "text-muted-foreground"}>
              {t("marketing.pages.tarifs.hero.billingAnnual")}
              <Badge variant="default" className="ml-2 text-[10px]">
                {t("marketing.pages.tarifs.hero.annualBadge")}
              </Badge>
            </Label>
          </div>
        </div>
      </Section>

      <Rule />

      {/* ── Section B — Plan cards ──────────────────────────────────────── */}
      <Section padding="md">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {VISIBLE_PLANS.map((slug) => {
            const isPopular = slug === "growth";
            const monthly = planMonthlyCents(slug)!;
            const displayCents = annual ? annualMonthlyEquivalentCents(monthly) : monthly;
            const setupCents = planSetupCents(slug) ?? 0;
            const features = t(`marketing.pages.tarifs.plans.features.${slug}`, { returnObjects: true }) as string[];

            return (
              <article
                key={slug}
                id={slug}
                className={
                  "flex flex-col border border-rule bg-background p-6 " +
                  (isPopular ? "ring-2 ring-accent-press" : "")
                }
              >
                <header className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-xl font-medium tracking-tight">{locText(PLAN_DISPLAY_NAMES[slug], locale)}</h3>
                    {isPopular && <Badge>{t("marketing.pages.tarifs.plans.popular")}</Badge>}
                  </div>
                  <p className="min-h-10 text-sm leading-snug text-muted-foreground">
                    {locText(PLAN_AUDIENCE[slug], locale)}
                  </p>
                </header>

                <div className="mt-6">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={annual ? "annual" : "monthly"}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.18 }}
                      className="text-4xl font-medium tabular-nums tracking-tight"
                    >
                      {formatMoney(displayCents, locale)}
                    </motion.div>
                  </AnimatePresence>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {annual
                      ? t("marketing.pages.tarifs.plans.perMonthAnnual")
                      : t("marketing.pages.tarifs.plans.perMonth")}
                  </p>
                  {setupCents > 0 && (
                    <p className="mt-2 text-xs">
                      {annual ? (
                        <Badge variant="secondary" className="text-[10px]">
                          {t("marketing.pages.tarifs.plans.setupFeeWaived")}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {t("marketing.pages.tarifs.plans.setupFee", { amount: formatMoney(setupCents, locale) })}
                        </span>
                      )}
                    </p>
                  )}
                </div>

                <ul className="mt-6 flex-1 space-y-2">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-snug">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden />
                      <span className="text-foreground">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button asChild className="mt-6 w-full">
                  <Link to={`/dpadmin?intent=signup&plan=${slug}${annual ? "&billing=annual" : ""}`}>
                    <Sparkles className="mr-2 h-4 w-4" aria-hidden />
                    {t("marketing.pages.tarifs.plans.ctaSelect", { plan: locText(PLAN_DISPLAY_NAMES[slug], locale) })}
                  </Link>
                </Button>
              </article>
            );
          })}
        </div>

        {/* Enterprise card — full-width below */}
        <article
          id="enterprise"
          className="mt-6 flex flex-col items-start justify-between gap-4 border border-dashed border-rule p-6 md:flex-row md:items-center"
        >
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-medium tracking-tight">{t("marketing.pages.tarifs.plans.enterpriseName")}</h3>
              <span className="text-sm text-muted-foreground">— {t("marketing.pages.tarifs.plans.enterprisePrice")}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{t("marketing.pages.tarifs.plans.enterpriseAudience")}</p>
            <ul className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
              {(t("marketing.pages.tarifs.plans.features.enterprise", { returnObjects: true }) as string[])
                .slice(0, 4)
                .map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden />
                    <span className="text-foreground">{f}</span>
                  </li>
                ))}
            </ul>
          </div>
          <Button asChild variant="outline" className="md:self-end">
            <Link to="/contact?subject=enterprise">
              {t("marketing.pages.tarifs.plans.ctaEnterprise")}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </article>
      </Section>

      <Rule />

      {/* ── Section C — Credit packs ────────────────────────────────────── */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.tarifs.credits.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.tarifs.credits.title")}
          </DisplayHeading>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            {t("marketing.pages.tarifs.credits.lead")}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CREDIT_PACKS.map((pack) => {
            const priceCents = creditPackCents(pack.sku);
            const smallest = creditPackCents(CREDIT_PACKS[0].sku) / CREDIT_PACKS[0].credits;
            const perCredit = priceCents / pack.credits;
            const savings = pack.sku === CREDIT_PACKS[0].sku ? 0 : Math.round((1 - perCredit / smallest) * 100);
            return (
              <article key={pack.sku} className="flex flex-col border border-rule bg-background p-6">
                <h3 className="text-2xl font-medium tabular-nums tracking-tight">
                  {t("marketing.pages.tarifs.credits.credits", { n: pack.credits.toLocaleString(intlLocale(locale)) })}
                </h3>
                <div className="mt-2 text-3xl font-medium tabular-nums">
                  {formatMoney(priceCents, locale)}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("marketing.pages.tarifs.credits.ratePerCredit", { rate: formatMoney(Math.round(perCredit * 1000), locale, { fraction: true }) })} / 1 000
                </p>
                {savings > 0 && (
                  <p className="mt-1 text-xs text-emerald-700">
                    {t("marketing.pages.tarifs.credits.savings", { pct: savings })}
                  </p>
                )}
                <Button asChild variant="outline" className="mt-auto pt-4">
                  <Link to={`/dpadmin?intent=signup&topup=${pack.sku}`}>
                    {t("marketing.pages.tarifs.credits.buyCta")}
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </article>
            );
          })}
        </div>

        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {t("marketing.pages.tarifs.credits.explainer")}
        </p>
      </Section>

      <Rule />

      {/* ── Section D — Comparison matrix ───────────────────────────────── */}
      <Section padding="md">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Eyebrow>{t("marketing.pages.tarifs.matrix.eyebrow")}</Eyebrow>
            <DisplayHeading level={2} className="mt-4">
              {t("marketing.pages.tarifs.matrix.title")}
            </DisplayHeading>
          </div>
          <Link
            to="/tarifs/comparatif"
            className="inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline"
          >
            {t("marketing.pages.tarifs.matrix.fullLink")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-foreground">
                <th className="px-3 py-3 text-left font-medium">{t("marketing.pages.tarifs.matrix.feature")}</th>
                {PLAN_SLUGS.map((slug) => (
                  <th key={slug} className="px-3 py-3 text-left font-medium">
                    {locText(PLAN_DISPLAY_NAMES[slug], locale)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAN_MATRIX.map((cat) => (
                <CategoryRows key={cat.id} category={cat} locale={locale} />
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Rule />

      {/* ── Section E — Pricing FAQ slice ───────────────────────────────── */}
      <Section padding="md">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.pages.tarifs.faq.eyebrow")}</Eyebrow>
          <DisplayHeading level={2} className="mt-4">
            {t("marketing.pages.tarifs.faq.title")}
          </DisplayHeading>
        </div>

        <Accordion type="single" collapsible className="mt-8 border-y border-rule">
          {tarifsFaq.map((entry) => {
            const anchor = entryAnchor(entry);
            return (
              <AccordionItem key={entry.id} value={entry.id} id={anchor} className="border-b border-rule last:border-b-0">
                <AccordionTrigger className="py-4 text-left text-base font-medium">
                  {faqQuestion(entry, locale)}
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-sm leading-relaxed text-muted-foreground">
                  {faqAnswer(entry, locale)}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <div className="mt-6">
          <Link to="/faq" className="inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline">
            {t("marketing.pages.tarifs.faq.fullLink")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </Section>

      {/* ── Section F — Final CTA banner ────────────────────────────────── */}
      <CTABanner
        headline={t("marketing.pages.tarifs.cta.headline")}
        subhead={t("marketing.pages.tarifs.cta.subhead")}
        primary={{ label: t("marketing.cta.startTrial"), to: "/dpadmin?intent=signup" }}
        secondary={{ label: t("marketing.cta.bookDemo"), to: "/contact" }}
        tone="ink"
      />
    </>
  );
}

// Tiny helper: render one matrix category header row + its rows.
function CategoryRows({ category, locale }: { category: typeof PLAN_MATRIX[number]; locale: Locale }) {
  return (
    <>
      <tr className="bg-muted/30">
        <th
          colSpan={PLAN_SLUGS.length + 1}
          scope="colgroup"
          className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {locText(category.label, locale)}
        </th>
      </tr>
      {category.rows.map((row) => (
        <tr key={row.id} className="border-b border-rule/50">
          <th scope="row" className="px-3 py-3 text-left font-normal text-foreground">
            {locText(row.label, locale)}
            {row.hint && <div className="mt-1 text-xs text-muted-foreground">{locText(row.hint, locale)}</div>}
          </th>
          {PLAN_SLUGS.map((slug) => {
            const cell = row.cells[slug];
            if (cell.kind === "boolean") {
              return (
                <td key={slug} className="px-3 py-3">
                  {cell.value ? (
                    <Check className="h-4 w-4 text-emerald-600" aria-label="Oui" />
                  ) : (
                    <X className="h-4 w-4 text-muted-foreground/50" aria-label="Non" />
                  )}
                </td>
              );
            }
            return (
              <td key={slug} className="px-3 py-3 text-foreground/90">
                {formatCell(cell, locale)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

